import React, { useEffect, useState, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  TablePagination,
  InputAdornment,
  useMediaQuery,
  useTheme , 
  FormControlLabel
   , Checkbox
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Bed,
  LocalHospital,
  CheckCircle,
  PersonAdd,
  People,
  PersonOutline,
  Search,
  Close,
} from '@mui/icons-material';

import { addWardWithBeds, loadInsurancesddb,updateWarddb } from '../BaseURL';
import axios from 'axios';
import Swal from 'sweetalert2';
import { ErrorMessage, successMessage } from '../Message';
import { loadBedsdb, loadWardsdb, deleteWard ,deleteBed } from '../BaseURL';
import {
  AdminPageHead, AdminSideNav, AdminWorkspace, ADMIN, BRAND, FONT_STACK,
  GRADIENT_GREEN, adminFieldSx, brandButtonSx, brandGhostButtonSx, EmptyState,
  useFitRows,
} from './adminUi';

// Shared pagination styling. MUI's default renders the label, the select and
// the range at three different sizes and colours, which is what made the
// "Rows per page: 5   1–5 of 7" strip look like leftover browser chrome.
const wbPaginationSx = {
  borderTop: `1px solid ${BRAND.line}`,
  '& .MuiTablePagination-toolbar': { minHeight: 48, px: 2 },
  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
    fontSize: 12, color: BRAND.inkMuted, fontWeight: 600, margin: 0,
  },
  '& .MuiTablePagination-select': { fontSize: 12, fontWeight: 700, color: BRAND.ink },
  '& .MuiTablePagination-actions': { marginLeft: 1.5 },
  '& .MuiIconButton-root': { color: BRAND.inkMuted, '&.Mui-disabled': { color: BRAND.line } },
};

// One definition for every table header cell on this screen. They were six
// copies of the same `fontWeight: 600, fontSize: {xs,sm}` block, which is how
// they drifted into different sizes from the rest of the admin tables.
const wbHeadCell = {
  fontSize: 10.5,
  fontWeight: 800,
  letterSpacing: '1px',
  textTransform: 'uppercase',
  color: BRAND.inkFaint,
  whiteSpace: 'nowrap',
  py: 1.25,
  borderBottom: `1px solid ${BRAND.line}`,
};

// Body cell: a hairline rule in the canvas tint rather than MUI's default grey,
// so the rows read as bands of one surface instead of a boxed grid.
const wbCell = {
  py: 1.25,
  borderBottom: `1px solid ${BRAND.canvas}`,
};

// Figures are tabular so the digits line up column to column. Without this a
// '1' is narrower than a '0' and a column of numbers visibly wobbles.
const wbNum = {
  fontSize: 13.5,
  fontWeight: 700,
  fontVariantNumeric: 'tabular-nums',
};


export default function WardsBeds() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [activeTab, setActiveTab] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState('');
  const [editingItem, setEditingItem] = useState(null);

  const [applyAll, setApplyAll] = useState(false);
  const [globalDailyRate, setGlobalDailyRate] = useState("");
  const [globalDailyTariff, setGlobalDailyTariff] = useState("");

  // Search and Pagination States
  const [wardSearchQuery, setWardSearchQuery] = useState('');
  const [wardPage, setWardPage] = useState(0);

  const [bedSearchQuery, setBedSearchQuery] = useState('');
  const [bedPage, setBedPage] = useState(0);

  //  The refs the auto-fit measures against. Declared here with the rest of the
  //  pagination state; the hook itself runs further down, after `wards` and
  //  `beds` exist for it to depend on.
  const wardTableRef = useRef(null);
  const wardPagerRef = useRef(null);
  const bedTableRef = useRef(null);
  const bedPagerRef = useRef(null);
  const [wardRowsChosen, setWardRowsChosen] = useState(null);
  const [bedRowsChosen, setBedRowsChosen] = useState(null);

 // The raw insurance list is stored but never read — `insuranceRates` below is
 // what the form actually binds to. Kept as a write-only slot rather than
 // deleted, because loadInsurances() populates both and dropping one would
 // change what that function does.
 const [, setInsurances] = useState([]);
 const [insuranceRates, setInsuranceRates] = useState([]);

  const [errors, setErrors] = useState({
    name: false,
    capacity: false,
    dailyRate: false
  });

  const [wards, setWards] = useState([]);
  const [beds, setBeds] = useState([]);

  // ── How many rows to show: whatever FITS ──────────────────────────────────
  //  Both tables opened at 5. On a laptop that drew a card about a third of the
  //  window tall, a wide band of empty page underneath it, and a pager reading
  //  "1–5 of 82" — eighty-two beds behind seventeen clicks, on a screen with room
  //  for four times as many at once.
  //
  //  Five was never a considered number; it is MUI's example value. The page size
  //  is measured from the real geometry now — see useFitRows in adminUi.js, which
  //  reads the container's own top and the height of a REAL row rather than
  //  subtracting a stack of constants that go stale.
  //
  //  A MANUAL CHOICE WINS AND KEEPS WINNING. Picking a size from the pager sets
  //  the override and switches auto-fit off; an auto-fit that quietly undid a
  //  deliberate choice on the next window resize would be worse than the fixed 5
  //  it replaces.
  //
  //  Only the VISIBLE tab measures. The hidden one is not laid out, so its
  //  container reports a top of 0 and any measurement taken from it is fiction.
  const wardRowsFit = useFitRows({
    containerRef: wardTableRef, pagerRef: wardPagerRef,
    enabled: wardRowsChosen == null && activeTab === 0,
    //  A ward row carries a capacity bar under its name and runs taller than a
    //  bed row. Only a starting estimate — the hook measures the real one as soon
    //  as there is a row on screen to measure.
    estimateRowHeight: 59,
    deps: [wards.length, wardSearchQuery, activeTab],
  });
  const bedRowsFit = useFitRows({
    containerRef: bedTableRef, pagerRef: bedPagerRef,
    enabled: bedRowsChosen == null && activeTab === 1,
    estimateRowHeight: 47,
    deps: [beds.length, bedSearchQuery, activeTab],
  });

  const wardRowsPerPage = wardRowsChosen ?? wardRowsFit;
  const bedRowsPerPage = bedRowsChosen ?? bedRowsFit;


const loadInsurances = async () => {
  try {
    const res = await axios.get(loadInsurancesddb);

    if (res.data.success) {
      const insurancesList = res.data.insurances;
      setInsurances(insurancesList);

      // Initialize rates only if not editing
      if (!editingItem) {
        setInsuranceRates(
          insurancesList.map(i => ({
            insuranceId: i.id,
            name: i.name,
            dailyTariff: 0,
            dailyRate: 0
          }))
        );
      } else {
        // If editing, prepare the structure but don't overwrite
        const baseRates = insurancesList.map(i => ({
          insuranceId: i.id,
          name: i.name,
          dailyTariff: 0,
          dailyRate: 0
        }));
        
        setInsuranceRates(baseRates);
      }
      
      return insurancesList;
    }
  } catch (error) {
    console.error("Error loading insurances:", error);
    ErrorMessage("Error", "Failed to load insurance companies");
    return [];
  }
};


  const loadWards = async () => {
    const res = await axios.get(loadWardsdb);
    setWards(res.data.wards);
  };

  const loadBeds = async () => {
    const res = await axios.get(loadBedsdb);
    setBeds(res.data.beds);
  };

  const [formData, setFormData] = useState({
    name: '',
    type: '',
    capacity: '',
    dailyRate: '',
    bedNumber: '',
    wardId: '',
    status: 'Available'
  });

  const bedStatuses = ['Available', 'Occupied', 'Maintenance', 'Reserved'];

  useEffect(() => {
    loadWards();
    loadBeds();
  }, []);


useEffect(() => {
  if (openDialog && dialogType === "ward") {
    const initializeInsuranceRates = async () => {
      // Load all insurances
      const res = await axios.get(loadInsurancesddb);
      
      if (res.data.success) {
        const allInsurances = res.data.insurances;
        setInsurances(allInsurances);
        
        // Map insurance rates
        const mappedRates = allInsurances.map(insurance => {
          // If editing, find the matching rate from the ward
          if (editingItem && editingItem.insuranceRates) {
            const existingRate = editingItem.insuranceRates.find(
              rate => rate.insuranceId === insurance.id
            );
            
            if (existingRate) {
              return {
                insuranceId: insurance.id,
                name: insurance.name,
                dailyTariff: existingRate.dailyTariff,
                dailyRate: existingRate.dailyRate
              };
            }
          }
          
          // Default values for new ward or missing rates
          return {
            insuranceId: insurance.id,
            name: insurance.name,
            dailyTariff: 0,
            dailyRate: 0
          };
        });
        
        setInsuranceRates(mappedRates);
      }
    };
    
    initializeInsuranceRates();
  }
}, [openDialog, dialogType, editingItem]);


useEffect(() => {
  if (!applyAll) return;

  setInsuranceRates(prev =>
    prev.map(row => ({
      ...row,
      dailyRate: globalDailyRate,
      dailyTariff: globalDailyTariff
    }))
  );
}, [applyAll, globalDailyRate, globalDailyTariff]);


  // Ward filtering and pagination

  const filteredWards = useMemo(() => {
    return wards.filter((ward) => {
      const query = wardSearchQuery.toLowerCase();
      return (
        (ward.name || '').toLowerCase().includes(query) ||
        (ward.status || '').toLowerCase().includes(query) ||
        (ward.id?.toString() || '').includes(query)
      );
    });
  }, [wards, wardSearchQuery]);

  const paginatedWards = useMemo(() => {
    return filteredWards.slice(
      wardPage * wardRowsPerPage,
      wardPage * wardRowsPerPage + wardRowsPerPage
    );
  }, [filteredWards, wardPage, wardRowsPerPage]);

  // Bed filtering and pagination
  const filteredBeds = useMemo(() => {
    return beds.filter((bed) => {
      const query = bedSearchQuery.toLowerCase();
      return (
        (bed.bedNumber || '').toLowerCase().includes(query) 
      );
    });
  }, [beds, bedSearchQuery]);

  const paginatedBeds = useMemo(() => {
    return filteredBeds.slice(
      bedPage * bedRowsPerPage,
      bedPage * bedRowsPerPage + bedRowsPerPage
    );
  }, [filteredBeds, bedPage, bedRowsPerPage]);

 const handleOpenDialog = async (type, item = null) => {
  setDialogType(type);
  setEditingItem(item);
  
  if (item) {
    setFormData(item);
    
    // If editing a ward, load and pre-fill insurance rates
    if (type === 'ward') {
      // First, load all insurances
      await loadInsurances();
      
      // Then, if the ward has insurance rates, pre-fill them
      if (item.insuranceRates && item.insuranceRates.length > 0) {
        // Map the ward's insurance rates to the insurances
        setInsuranceRates(prevInsurances => {
          return prevInsurances.map(insurance => {
            // Find matching rate for this insurance
            const wardRate = item.insuranceRates.find(
              rate => rate.insuranceId === insurance.insuranceId
            );
            
            if (wardRate) {
              return {
                insuranceId: insurance.insuranceId,
                name: insurance.name,
                dailyTariff: wardRate.dailyTariff || 0,
                dailyRate: wardRate.dailyRate || 0
              };
            }
            
            // If no rate found, return default
            return {
              insuranceId: insurance.insuranceId,
              name: insurance.name,
              dailyTariff: 0,
              dailyRate: 0
            };
          });
        });
      }
    }
  } else {
    setFormData({
      name: '',
      type: '',
      capacity: '',
      dailyRate: '',
      bedNumber: '',
      wardId: '',
      status: 'Available'
    });
  }
  
  setOpenDialog(true);
};

  const handleCloseDialog = () => {
  setOpenDialog(false);
  setEditingItem(null);
  setApplyAll(false);
  setGlobalDailyRate("");
  setGlobalDailyTariff("");
  setInsuranceRates([]);
  setErrors({
    name: false,
    capacity: false,
    dailyRate: false
  });
};

const handleSaveWard = async () => {
  // 1️⃣ Basic ward validation
  const newErrors = {
    name: !formData.name.trim(),
    capacity: !formData.capacity || Number(formData.capacity) <= 0,
  };

  setErrors(newErrors);

  if (Object.values(newErrors).includes(true)) {
    ErrorMessage("Error", "Please correct the errors before saving.");
    return;
  }

  // 2️⃣ Insurance pricing validation
  const invalidInsurance = insuranceRates.some(
    (r) =>
      r.dailyRate === "" ||
      r.dailyTariff === "" ||
      isNaN(Number(r.dailyRate)) ||
      isNaN(Number(r.dailyTariff))
  );

  if (invalidInsurance) {
    ErrorMessage(
      "Error",
      "Daily Rate and Daily Tariff cannot be empty for any insurance."
    );
    return;
  }

  // 3️⃣ Confirm action
  const isEditing = editingItem && editingItem.id;
  const actionText = isEditing ? "Update" : "Create";
  
  const result = await Swal.fire({
    title: `${actionText} Ward?`,
    text: `Do you want to ${actionText.toLowerCase()} ward "${formData.name}" with ${formData.capacity} beds?`,
    icon: "question",
    showCancelButton: true,
    confirmButtonText: `Yes, ${actionText}`,
    cancelButtonText: "Cancel",
  });

  if (!result.isConfirmed) return;

  // 4️⃣ Prepare data
  const wardData = {
    name: formData.name,
    capacity: Number(formData.capacity),
    status: "Active",
    insuranceRates: insuranceRates.map((r) => ({
      insuranceId: r.insuranceId,
      insuranceName: r.name,
      dailyRate: Number(r.dailyRate),
      dailyTariff: Number(r.dailyTariff),
    })),
  };

  // 5️⃣ Submit to backend
  try {
    let response;
    
    if (isEditing) {
      // UPDATE existing ward
      response = await axios.post(updateWarddb, {
        wardId: editingItem.id,
        ...wardData
      });
    } else {
      // CREATE new ward
      response = await axios.post(addWardWithBeds, wardData);
    }

    if (response.data.success) {
      successMessage(
        "Success", 
        isEditing 
          ? "Ward and insurance rates updated successfully" 
          : "Ward created with insurance pricing"
      );
      handleCloseDialog();
      loadWards();
      loadBeds();
    } else {
      ErrorMessage("Error", response.data.message);
    }
  } catch (error) {
    console.error(`Error ${isEditing ? 'updating' : 'creating'} ward:`, error);
    ErrorMessage("Error", `Failed to ${isEditing ? 'update' : 'create'} ward`);
  }
};


  const handleSaveBed = () => {
    const ward = wards.find(w => w.id === parseInt(formData.wardId));
    if (editingItem) {
      setBeds(beds.map(b => b.id === editingItem.id ? {
        ...b,
        ...formData,
        wardName: ward?.name,
        dailyRate: ward?.dailyRate
      } : b));
    } else {
      const newBed = {
        id: beds.length + 1,
        ...formData,
        wardName: ward?.name,
        patientName: '',
        admissionDate: '',
        dailyRate: ward?.dailyRate
      };
      setBeds([...beds, newBed]);
    }
    handleCloseDialog();
  };

 const handleDeleteWard = async (id) => {
  const ward = wards.find(w => w.id === id);
  
  if (!ward) {
    ErrorMessage("Error", "Ward not found");
    return;
  }

  // Check if ward has occupied beds
  if (ward.occupied > 0) {
    ErrorMessage(
      "Cannot Delete",
      `This ward has ${ward.occupied} occupied bed(s). Please discharge or transfer patients before deleting.`
    );
    return;
  }

  const result = await Swal.fire({
    title: "Delete Ward?",
    html: `
      <div style="text-align: left;">
        <p><strong>Ward:</strong> ${ward.name}</p>
        <p><strong>Capacity:</strong> ${ward.capacity} beds</p>
        <p><strong>Available Beds:</strong> ${ward.capacity - ward.occupied}</p>
        <p><strong>Insurance Rates:</strong> ${ward.insuranceRates?.length || 0} configured</p>
        <br/>
        <p style="color: #d32f2f; font-weight: 600;">
          ⚠️ This will permanently remove:
        </p>
        <ul style="color: #666;">
          <li>The ward</li>
          <li>All ${ward.capacity} beds</li>
          <li>All insurance pricing configurations</li>
        </ul>
      </div>
    `,
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#d33",
    cancelButtonColor: "#3085d6",
    confirmButtonText: "Yes, delete it",
    cancelButtonText: "Cancel"
  });

  if (!result.isConfirmed) return;

  try {
    const response = await axios.post(deleteWard, { wardId: id });

    if (response.data.success) {
      successMessage("Deleted", response.data.message || "Ward removed successfully");
      await loadWards();
      await loadBeds();
    } else {
      ErrorMessage("Error", response.data.message);
    }
  } catch (error) {
    console.error("Delete ward error:", error);
    ErrorMessage("Error", "Failed to delete ward");
  }
};

  
const handleDeleteBed = async (id) => {
  const bed = beds.find(b => b.id === id);
  
  if (!bed) {
    ErrorMessage("Error", "Bed not found");
    return;
  }

  // Check bed status
  if (bed.status === 'Occupied') {
    ErrorMessage(
      "Cannot Delete",
      "This bed is currently occupied. Please discharge the patient first."
    );
    return;
  }

  const result = await Swal.fire({
    title: "Delete Bed?",
    html: `
      <div style="text-align: left;">
        <p><strong>Bed Number:</strong> ${bed.bedNumber}</p>
        <p><strong>Ward:</strong> ${bed.wardName}</p>
        <p><strong>Status:</strong> ${bed.status}</p>
        <br/>
        <p style="color: #d32f2f; font-weight: 600;">
          ⚠️ This will:
        </p>
        <ul style="color: #666;">
          <li>Permanently remove this bed</li>
          <li>Reduce ward capacity by 1</li>
        </ul>
      </div>
    `,
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#d33",
    cancelButtonColor: "#3085d6",
    confirmButtonText: "Yes, delete it",
    cancelButtonText: "Cancel"
  });

  if (!result.isConfirmed) return;

  try {
    const res = await axios.post(deleteBed, { bedId: id });

    if (res.data.success) {
      successMessage("Deleted", res.data.message || "Bed removed and ward capacity updated");
      await loadBeds();
      await loadWards(); // ✅ Reload wards to show updated capacity
    } else {
      ErrorMessage("Error", res.data.message);
    }

  } catch (err) {
    console.error("Delete bed error:", err);
    ErrorMessage("Error", err.response?.data?.message || "Server error");
  }
};


  const getStatusColor = (status) => {
    switch (status) {
      case 'Available': return 'success';
      case 'Occupied': return 'error';
      case 'Maintenance': return 'warning';
      case 'Reserved': return 'info';
      default: return 'default';
    }
  };

  // Derived once. These were recomputed inline four times inside the stat
  // cards — including `reduce` twice in the same expression to work out the
  // occupancy rate.
  const totalCapacity = wards.reduce((acc, w) => acc + (Number(w.capacity) || 0), 0);
  const totalOccupied = wards.reduce((acc, w) => acc + (Number(w.occupied) || 0), 0);
  const occupancyRate = totalCapacity > 0 ? (totalOccupied / totalCapacity) * 100 : 0;

  return (
    <Box>
      {/* One title, not three. The shell prints "Wards & Beds" above this
          screen; the screen then printed "Wards & Beds Management" as an h4 and
          repeated the same explanation again in an info Alert. All three said
          the same thing and cost about 180px before any content appeared. The
          explanation is the subtitle now. */}
      <AdminPageHead
        icon={LocalHospital}
        title="Wards & Beds"
        subtitle="Wards, bed allocation and pricing — changes sync with Admissions, Billing and the Ward Dashboard"
        actions={
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog(activeTab === 0 ? 'ward' : 'bed')}
            sx={{ ...brandButtonSx, width: { xs: '100%', sm: 'auto' }, py: 1, px: 2.25, fontSize: 13.5, whiteSpace: 'nowrap' }}
          >
            {activeTab === 0 ? 'Add Ward' : 'Add Bed'}
          </Button>
        }
      />

      {/* Sections down the side rather than a horizontal tab strip, with the
          live counts and the occupancy summary in the nav itself. */}
      <AdminWorkspace
        nav={
          <AdminSideNav
            value={activeTab}
            onChange={(e, v) => setActiveTab(v)}
            items={[
              {
                icon: LocalHospital,
                label: 'Wards',
                caption: 'Capacity and daily pricing',
                badge: wards.length,
              },
              {
                icon: Bed,
                label: 'Beds',
                caption: 'Individual beds and their status',
                badge: beds.length,
              },
            ]}
            stats={[
              { label: 'Capacity', value: totalCapacity },
              { label: 'Occupied', value: totalOccupied, accent: totalOccupied > 0 ? BRAND.warning : undefined },
              { label: 'Available', value: Math.max(0, totalCapacity - totalOccupied), accent: BRAND.green },
              { label: 'Occupancy', value: `${occupancyRate.toFixed(0)}%` },
            ]}
          />
        }
      >

      {/* Wards Tab */}
      {activeTab === 0 && (
        <Box>

          <Paper sx={{ boxShadow: ADMIN.shadow, border: `1px solid ${BRAND.line}`, borderRadius: ADMIN.radius, overflow: 'hidden' }}>
            <Box sx={{ p: { xs: 1.5, sm: 2 }, borderBottom: `1px solid ${BRAND.line}` }}>
              <TextField
                fullWidth
                placeholder="Search wards…"
                value={wardSearchQuery}
                onChange={(e) => {
                  setWardSearchQuery(e.target.value);
                  setWardPage(0);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ fontSize: 18, color: BRAND.inkFaint }} />
                    </InputAdornment>
                  ),
                  endAdornment: wardSearchQuery ? (
                    <InputAdornment position="end">
                      <IconButton size="small" aria-label="Clear search" onClick={() => { setWardSearchQuery(''); setWardPage(0); }}>
                        <Close sx={{ fontSize: 16, color: BRAND.inkFaint }} />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
                // Was size="big" — not a value MUI accepts, so it fell back to
                // the default and warned in the console on every render.
                size="small"
                sx={adminFieldSx}
              />
            </Box>

            <TableContainer ref={wardTableRef} sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: { xs: 500, sm: 650, md: 750 } }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: BRAND.canvas }}>
                    <TableCell sx={{ ...wbHeadCell, minWidth: { xs: 120, sm: 150 } }}>
                      Ward
                    </TableCell>
                    <TableCell align="center" sx={{ ...wbHeadCell, minWidth: 80, display: { xs: 'none', sm: 'table-cell' } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                        <People sx={{ fontSize: 15, color: BRAND.navy }} />
                        Cap.
                      </Box>
                    </TableCell>
                    <TableCell align="center" sx={{ ...wbHeadCell, minWidth: 80, display: { xs: 'none', sm: 'table-cell' } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                        <PersonOutline sx={{ fontSize: 15, color: BRAND.warning }} />
                        Occ.
                      </Box>
                    </TableCell>
                    <TableCell align="center" sx={{ ...wbHeadCell, minWidth: 80 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                        <CheckCircle sx={{ fontSize: 15, color: BRAND.green }} />
                        <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>Avail.</Box>
                      </Box>
                    </TableCell>
                    <TableCell align="center" sx={{ ...wbHeadCell, minWidth: 80 }}>
                      Status
                    </TableCell>
                    <TableCell align="center" sx={{ ...wbHeadCell, minWidth: { xs: 80, sm: 100 } }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedWards.length > 0 ? (
                    paginatedWards.map((ward) => {
                      const cap  = Number(ward.capacity) || 0;
                      const occ  = Number(ward.occupied) || 0;
                      const free = Math.max(0, cap - occ);
                      const pct  = cap > 0 ? Math.min(100, (occ / cap) * 100) : 0;
                      const isFull = cap > 0 && free === 0;

                      return (
                      <TableRow key={ward.id} sx={{ '&:hover': { bgcolor: BRAND.canvas }, '&:last-child td': { borderBottom: 0 } }}>
                        <TableCell sx={wbCell}>
                          <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: BRAND.ink, lineHeight: 1.3 }}>
                            {ward.name}
                          </Typography>
                          {/* Occupancy as a bar, not three numbers to subtract
                              in your head. Thin, one hue, direct-labelled —
                              and it is the only thing here that answers "how
                              full is this ward?" at a glance. */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: .6, maxWidth: 260 }}>
                            <Box sx={{ flex: 1, height: 5, borderRadius: 3, background: BRAND.canvas, overflow: 'hidden', minWidth: 60 }}>
                              <Box sx={{
                                width: `${pct}%`, height: '100%', borderRadius: 3,
                                background: isFull ? BRAND.danger : BRAND.navy,
                                transition: 'width .25s ease',
                              }} />
                            </Box>
                            <Typography sx={{ fontSize: 10.5, color: BRAND.inkMuted, fontWeight: 600, fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
                              {occ}/{cap}
                            </Typography>
                          </Box>
                        </TableCell>

                        {/* Plain tabular figures. The three coloured circles
                            these replace were the same weight as each other, so
                            the eye had nothing to latch onto — and the pale-red
                            one was 'Occupied', which meant a ward with 0
                            patients displayed a red badge. Empty is not an
                            error; FULL is, and that is what is coloured now. */}
                        <TableCell align="center" sx={{ ...wbCell, display: { xs: 'none', sm: 'table-cell' } }}>
                          <Typography sx={{ ...wbNum, color: BRAND.inkMuted }}>{cap}</Typography>
                        </TableCell>
                        <TableCell align="center" sx={{ ...wbCell, display: { xs: 'none', sm: 'table-cell' } }}>
                          <Typography sx={{ ...wbNum, color: occ > 0 ? BRAND.ink : BRAND.inkFaint }}>{occ}</Typography>
                        </TableCell>
                        <TableCell align="center" sx={wbCell}>
                          <Typography sx={{
                            ...wbNum,
                            color: isFull ? BRAND.danger : BRAND.greenDark,
                            fontWeight: isFull ? 800 : 700,
                          }}>
                            {isFull ? 'Full' : free}
                          </Typography>
                        </TableCell>

                        <TableCell align="center" sx={wbCell}>
                          {/* A dot plus the word, on a tint — rather than a
                              solid dark-green block, which was the heaviest
                              thing in the row and drew the eye away from the
                              numbers that matter. */}
                          <Box sx={{
                            display: 'inline-flex', alignItems: 'center', gap: .625,
                            px: 1, py: .3, borderRadius: '20px',
                            bgcolor: ward.status === 'Active' ? BRAND.greenSoft : BRAND.canvas,
                          }}>
                            <Box sx={{
                              width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
                              bgcolor: ward.status === 'Active' ? BRAND.green : BRAND.inkFaint,
                            }} />
                            <Typography sx={{
                              fontSize: 11, fontWeight: 700,
                              color: ward.status === 'Active' ? BRAND.greenDark : BRAND.inkMuted,
                            }}>
                              {ward.status}
                            </Typography>
                          </Box>
                        </TableCell>

                        <TableCell align="center" sx={wbCell}>
                          {/* Revealed on row hover on a mouse, always visible on
                              touch — a hover-only control is unreachable on a
                              tablet, which is what half the ward staff use. */}
                          <Stack direction="row" spacing={.25} justifyContent="center" className="wb-actions">
                            <Tooltip title="Edit ward" arrow>
                              <IconButton
                                size="small"
                                onClick={() => handleOpenDialog('ward', ward)}
                                sx={{ color: BRAND.inkMuted, '&:hover': { color: BRAND.navy, bgcolor: BRAND.navySoft } }}
                              >
                                <Edit sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete ward" arrow>
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteWard(ward.id)}
                                sx={{ color: BRAND.inkFaint, '&:hover': { color: BRAND.danger, bgcolor: '#FDECEC' } }}
                              >
                                <Delete sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ borderBottom: 'none' }}>
                        <EmptyState
                          icon={LocalHospital}
                          title="No wards found"
                          body={wardSearchQuery
                            ? `Nothing matches “${wardSearchQuery}”.`
                            : 'No wards have been created yet.'}
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/*  The auto-fitted size is offered as an option so the select has
                 something to show for it — without it MUI renders a blank box
                 when the current value is not in the list, which reads as a bug.
                 "Fit" is first because it is what the table is doing.  */}
            <TablePagination
              ref={wardPagerRef}
              rowsPerPageOptions={[
                { value: wardRowsPerPage, label: wardRowsChosen == null ? `Fit (${wardRowsFit})` : String(wardRowsPerPage) },
                ...[5, 10, 25, 50].filter((n) => n !== wardRowsPerPage),
              ]}
              component="div"
              count={filteredWards.length}
              rowsPerPage={wardRowsPerPage}
              page={wardPage}
              onPageChange={(e, newPage) => setWardPage(newPage)}
              onRowsPerPageChange={(e) => {
                setWardRowsChosen(parseInt(e.target.value, 10));
                setWardPage(0);
              }}
              sx={wbPaginationSx}
            />
          </Paper>
        </Box>
      )}

      {/* Beds Tab */}
      {activeTab === 1 && (
        <Box>
          {/* The "Bed Management" heading is gone — a fourth title saying what
              the nav item beside it already says. */}
          <Paper sx={{ boxShadow: ADMIN.shadow, border: `1px solid ${BRAND.line}`, borderRadius: ADMIN.radius, overflow: 'hidden' }}>
            <Box sx={{ p: { xs: 1.5, sm: 2 }, borderBottom: `1px solid ${BRAND.line}` }}>
              <TextField
                fullWidth
                placeholder="Search beds by number or ward…"
                value={bedSearchQuery}
                onChange={(e) => {
                  setBedSearchQuery(e.target.value);
                  setBedPage(0);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ fontSize: 18, color: BRAND.inkFaint }} />
                    </InputAdornment>
                  ),
                  endAdornment: bedSearchQuery ? (
                    <InputAdornment position="end">
                      <IconButton size="small" aria-label="Clear search" onClick={() => { setBedSearchQuery(''); setBedPage(0); }}>
                        <Close sx={{ fontSize: 16, color: BRAND.inkFaint }} />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
                // Also size="big" here — same invalid value as the ward search.
                size="small"
                sx={adminFieldSx}
              />
            </Box>

            <TableContainer ref={bedTableRef} sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: { xs: 650, md: 750 } }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: BRAND.canvas }}>
                    <TableCell sx={{ ...wbHeadCell, minWidth: 120 }}>Bed</TableCell>
                    <TableCell sx={{ ...wbHeadCell, minWidth: 120 }}>Ward</TableCell>
                    <TableCell align="center" sx={{ ...wbHeadCell, minWidth: 100 }}>Status</TableCell>
                    <TableCell sx={{ ...wbHeadCell, minWidth: 150, display: { xs: 'none', md: 'table-cell' } }}>
                      Patient
                    </TableCell>
                    <TableCell sx={{ ...wbHeadCell, minWidth: 120, display: { xs: 'none', lg: 'table-cell' } }}>
                      Admitted
                    </TableCell>
                    <TableCell align="center" sx={{ ...wbHeadCell, minWidth: 100 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedBeds.length > 0 ? (
                    paginatedBeds.map((bed) => {
                      // Occupied beds are the ones that matter on this screen,
                      // so they carry the weight; an available bed is quiet.
                      const occupied = bed.status !== 'Available';
                      return (
                      <TableRow key={bed.id} sx={{ '&:hover': { bgcolor: BRAND.canvas }, '&:last-child td': { borderBottom: 0 } }}>
                        <TableCell sx={wbCell}>
                          <Typography sx={{ fontSize: 13, fontWeight: 700, color: BRAND.ink, fontVariantNumeric: 'tabular-nums' }}>
                            {bed.bedNumber}
                          </Typography>
                          {/* Only on narrow screens, where the Patient column
                              is hidden — otherwise it is the same value twice. */}
                          {bed.patientName && (
                            <Typography noWrap sx={{ display: { xs: 'block', md: 'none' }, fontSize: 11, color: BRAND.inkMuted }}>
                              {bed.patientName}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={wbCell}>
                          <Typography sx={{ fontSize: 12.5, color: BRAND.inkMuted, fontWeight: 600 }}>
                            {bed.wardName}
                          </Typography>
                        </TableCell>
                        <TableCell align="center" sx={wbCell}>
                          <Chip
                            label={bed.status}
                            color={getStatusColor(bed.status)}
                            size="small"
                            variant={occupied ? 'filled' : 'outlined'}
                            sx={{ height: 20, fontSize: 10.5, fontWeight: 700 }}
                          />
                        </TableCell>
                        <TableCell sx={{ ...wbCell, display: { xs: 'none', md: 'table-cell' } }}>
                          <Typography sx={{ fontSize: 12.5, color: bed.patientName ? BRAND.ink : BRAND.inkFaint, fontWeight: bed.patientName ? 600 : 400 }}>
                            {bed.patientName || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ ...wbCell, display: { xs: 'none', lg: 'table-cell' } }}>
                          <Typography sx={{ fontSize: 12, color: BRAND.inkMuted, fontVariantNumeric: 'tabular-nums' }}>
                            {bed.admissionDate || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center" sx={wbCell}>
                          <Stack direction="row" spacing={.25} justifyContent="center">
                            {bed.status === 'Available' && (
                              <Tooltip title="Assign patient" arrow>
                                <IconButton size="small" sx={{ color: BRAND.inkMuted, '&:hover': { color: BRAND.navy, bgcolor: BRAND.navySoft } }}>
                                  <PersonAdd sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Delete bed" arrow>
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteBed(bed.id)}
                                sx={{ color: BRAND.inkFaint, '&:hover': { color: BRAND.danger, bgcolor: '#FDECEC' } }}
                              >
                                <Delete sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ borderBottom: 'none' }}>
                        <EmptyState
                          icon={Bed}
                          title="No beds found"
                          body={bedSearchQuery
                            ? `Nothing matches “${bedSearchQuery}”.`
                            : 'No beds exist yet — they are created with a ward, from its capacity.'}
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              ref={bedPagerRef}
              rowsPerPageOptions={[
                { value: bedRowsPerPage, label: bedRowsChosen == null ? `Fit (${bedRowsFit})` : String(bedRowsPerPage) },
                ...[5, 10, 25, 50, 100].filter((n) => n !== bedRowsPerPage),
              ]}
              component="div"
              count={filteredBeds.length}
              rowsPerPage={bedRowsPerPage}
              page={bedPage}
              onPageChange={(e, newPage) => setBedPage(newPage)}
              onRowsPerPageChange={(e) => {
                setBedRowsChosen(parseInt(e.target.value, 10));
                setBedPage(0);
              }}
              sx={wbPaginationSx}
            />
          </Paper>
        </Box>
      )}

      </AdminWorkspace>

      {/* Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : ADMIN.radius, fontFamily: FONT_STACK } }}
      >
       {/* Was a flat #19b934 green — a fifth distinct green in the product, and
           the loudest thing on the screen. Same navy lockup as the other admin
           dialogs, with the icon carrying the colour instead of the whole bar. */}
       <DialogTitle sx={{ p: 0 }}>
          <Box sx={{
            background: `linear-gradient(135deg, ${BRAND.navy} 0%, ${BRAND.navyDark} 100%)`,
            color: '#fff',
            px: { xs: 2, sm: 2.5 }, py: 2,
            display: 'flex', alignItems: 'center', gap: 1.75,
          }}>
            <Box sx={{
              width: 36, height: 36, borderRadius: '10px', flexShrink: 0,
              background: GRADIENT_GREEN, display: 'grid', placeItems: 'center',
            }}>
              {dialogType === 'ward'
                ? <LocalHospital sx={{ fontSize: 19 }} />
                : <Bed sx={{ fontSize: 19 }} />}
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography sx={{ fontSize: 16, fontWeight: 800, lineHeight: 1.25 }}>
                {dialogType === 'ward'
                  ? (editingItem ? `Edit Ward — ${editingItem.name}` : 'Add New Ward')
                  : (editingItem ? 'Edit Bed' : 'Add New Bed')}
              </Typography>
              <Typography sx={{ fontSize: 11.5, color: 'rgba(255,255,255,.75)', mt: .2 }}>
                {dialogType === 'ward'
                  ? 'Capacity, and the daily rate each scheme is charged'
                  : 'Which ward this bed belongs to, and its status'}
              </Typography>
            </Box>
            <IconButton
              size="small" onClick={handleCloseDialog} aria-label="Close"
              sx={{ color: '#fff', flexShrink: 0, bgcolor: 'rgba(255,255,255,.12)', '&:hover': { bgcolor: 'rgba(255,255,255,.24)' } }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ bgcolor: BRAND.canvas, px: { xs: 2, sm: 2.5 } }}>
          <Box sx={{ pt: 2.5 }}>
            {dialogType === 'ward' ? (
  // Real CSS Grid. Was `<Grid container columns={{xs:4,sm:8,md:12}}>` with
  // `<Grid item xs={12} size={6}>` children — MUI 7 ignores `item`/`xs`, so the
  // layout was whatever `size` alone produced, which is why Ward Name and
  // Capacity sat in one cramped row and the rate fields in another.
  <Box sx={{
    display: 'grid', gap: 2,
    gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' },
  }}>

    {/* Ward Name */}
    <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
      <TextField
        fullWidth
        size="small"
        label="Ward Name"
        value={formData.name}
        error={errors.name}
        helperText={errors.name ? "Ward name is required" : " "}
        onChange={(e) =>
          setFormData({ ...formData, name: e.target.value })
        }
        sx={adminFieldSx}
      />
    </Box>

    {/* Capacity */}
    <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
      <TextField
        fullWidth
        size="small"
        type="number"
        label="Capacity"
        value={formData.capacity}
        error={errors.capacity}
        helperText={errors.capacity ? "Capacity must be positive" : "Number of beds created in this ward"}
        onChange={(e) =>
          setFormData({ ...formData, capacity: e.target.value })
        }
        sx={adminFieldSx}
      />
    </Box>

    {/* ── Pricing ──
        The two "apply to all" fields and the checkbox that arms them were
        three loose controls above an unrelated table. They are one block now,
        because they only make sense together: the fields do nothing unless the
        checkbox is ticked, which the old layout gave no hint of. */}
    <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
      <Box sx={{
        border: `1px solid ${BRAND.line}`,
        borderRadius: ADMIN.radiusSm,
        background: BRAND.surface,
        p: { xs: 1.5, sm: 2 },
      }}>
        <Typography sx={{
          fontSize: 11, fontWeight: 800, letterSpacing: '1px',
          textTransform: 'uppercase', color: BRAND.inkFaint, mb: 1.5,
        }}>
          Daily pricing
        </Typography>

        <FormControlLabel
          sx={{ ml: 0, mb: applyAll ? 1.75 : 1 }}
          control={
            <Checkbox
              size="small"
              checked={applyAll}
              onChange={(e) => {
                const checked = e.target.checked;
                setApplyAll(checked);

                if (!checked) {
                  // 🔥 Clear global inputs only
                  setGlobalDailyRate("");
                  setGlobalDailyTariff("");
                }
              }}
              sx={{ color: BRAND.inkFaint, '&.Mui-checked': { color: BRAND.green } }}
            />
          }
          label={
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: BRAND.ink }}>
              Charge every scheme the same rate
            </Typography>
          }
        />

        {/* Only shown when armed. Two always-visible fields that silently did
            nothing unless the box was ticked was the confusing part. */}
        {applyAll && (
          <Box sx={{
            display: 'grid', gap: 1.75, mb: 2,
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' },
          }}>
            <TextField
              fullWidth size="small" type="number"
              label="Daily Tariff — all schemes"
              value={globalDailyTariff}
              onChange={(e) => setGlobalDailyTariff(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start">GH₵</InputAdornment> }}
              sx={adminFieldSx}
            />
            <TextField
              fullWidth size="small" type="number"
              label="Daily Rate — all schemes"
              value={globalDailyRate}
              onChange={(e) => setGlobalDailyRate(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start">GH₵</InputAdornment> }}
              sx={adminFieldSx}
            />
          </Box>
        )}

        <TableContainer sx={{ border: `1px solid ${BRAND.line}`, borderRadius: ADMIN.radiusSm, opacity: applyAll ? .5 : 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: BRAND.canvas }}>
                <TableCell sx={wbHeadCell}>Insurance</TableCell>
                <TableCell sx={wbHeadCell}>Daily Tariff (GH₵)</TableCell>
                <TableCell sx={wbHeadCell}>Daily Rate (GH₵)</TableCell>
              </TableRow>
            </TableHead>

         <TableBody>
  {insuranceRates.map((row, index) => (
    <TableRow key={row.insuranceId}>
      <TableCell sx={{ fontSize: 12.5, fontWeight: 600, color: BRAND.ink }}>{row.name}</TableCell>

      <TableCell sx={{ width: 130 }}>
        <TextField
          size="small"
          type="number"
          fullWidth
          disabled={applyAll}
          value={row.dailyTariff}
          onChange={(e) => {
            const updated = [...insuranceRates];
            updated[index].dailyTariff = e.target.value;
            setInsuranceRates(updated);
          }}
          inputProps={{ min: 0, step: 0.01 }}
          sx={adminFieldSx}
        />
      </TableCell>

      <TableCell sx={{ width: 130 }}>
        <TextField
          size="small"
          type="number"
          fullWidth
          disabled={applyAll}
          value={row.dailyRate}
          onChange={(e) => {
            const updated = [...insuranceRates];
            updated[index].dailyRate = e.target.value;
            setInsuranceRates(updated);
          }}
          inputProps={{ min: 0, step: 0.01 }}
          sx={adminFieldSx}
        />
      </TableCell>
    </TableRow>
  ))}
</TableBody>
          </Table>
        </TableContainer>

        {applyAll && (
          <Typography sx={{ fontSize: 11, color: BRAND.inkMuted, mt: 1 }}>
            Per-scheme rates are ignored while this is on.
          </Typography>
        )}
      </Box>
    </Box>

  </Box>
) : (

              <Box sx={{ display: 'grid', gap: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Bed Number"
                  value={formData.bedNumber}
                  onChange={(e) => setFormData({ ...formData, bedNumber: e.target.value })}
                  sx={adminFieldSx}
                />
                <FormControl fullWidth size="small" sx={adminFieldSx}>
                  <InputLabel shrink>Ward</InputLabel>
                  <Select
                    notched
                    value={formData.wardId}
                    label="Ward"
                    onChange={(e) => setFormData({ ...formData, wardId: e.target.value })}
                  >
                    {wards.map((ward) => (
                      <MenuItem key={ward.id} value={ward.id}>
                        {ward.name}{ward.type ? ` — ${ward.type}` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth size="small" sx={adminFieldSx}>
                  <InputLabel shrink>Status</InputLabel>
                  <Select
                    notched
                    value={formData.status}
                    label="Status"
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    {bedStatuses.map((status) => (
                      <MenuItem key={status} value={status}>{status}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{
          px: { xs: 2, sm: 2.5 }, py: 1.75,
          bgcolor: BRAND.surface,
          borderTop: `1px solid ${BRAND.line}`,
          gap: 1.25,
          flexDirection: { xs: 'column-reverse', sm: 'row' },
          '& > *': { width: { xs: '100%', sm: 'auto' }, ml: { xs: '0 !important', sm: undefined } },
        }}>
          <Button
            variant="outlined"
            onClick={handleCloseDialog}
            sx={{ ...brandGhostButtonSx, py: 1, px: 2.5, fontSize: 13.5 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={dialogType === 'ward' ? handleSaveWard : handleSaveBed}
            sx={{ ...brandButtonSx, py: 1, px: 2.5, fontSize: 13.5 }}
          >
            {editingItem ? 'Save changes' : (dialogType === 'ward' ? 'Create ward' : 'Create bed')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}